import { useState, useEffect } from 'react';
import {
  Box,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Typography,
  Paper,
  Chip,
  Button,
} from '@mui/material';
import { validateCronExpression, describeCronExpression } from '../../utils/cronValidation';

interface CronBuilderProps {
  value: string;
  onChange: (value: string) => void;
  error?: boolean;
  helperText?: string;
}

/**
 * Visual cron expression builder component.
 * Allows users to build cron expressions using a visual interface.
 */
export const CronBuilder = ({ value, onChange, error, helperText }: CronBuilderProps) => {
  const [minute, setMinute] = useState('0');
  const [hour, setHour] = useState('0');
  const [dayOfMonth, setDayOfMonth] = useState('*');
  const [month, setMonth] = useState('*');
  const [dayOfWeek, setDayOfWeek] = useState('*');
  const [customMode, setCustomMode] = useState(false);
  const [customExpression, setCustomExpression] = useState('');

  // Parse existing cron expression on mount or when value changes
  useEffect(() => {
    if (value && validateCronExpression(value)) {
      const parts = value.trim().split(/\s+/);
      if (parts.length === 5) {
        setMinute(parts[0]);
        setHour(parts[1]);
        setDayOfMonth(parts[2]);
        setMonth(parts[3]);
        setDayOfWeek(parts[4]);
        setCustomExpression(value);
      }
    }
  }, [value]);

  // Update cron expression when fields change
  useEffect(() => {
    if (!customMode) {
      const expression = `${minute} ${hour} ${dayOfMonth} ${month} ${dayOfWeek}`;
      if (expression !== customExpression) {
        setCustomExpression(expression);
        onChange(expression);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [minute, hour, dayOfMonth, month, dayOfWeek, customMode]);

  const handleCustomChange = (newValue: string) => {
    setCustomExpression(newValue);
    if (validateCronExpression(newValue)) {
      onChange(newValue);
    }
  };

  const handlePreset = (preset: string) => {
    setCustomMode(false);
    setCustomExpression(preset);
    onChange(preset);
    const parts = preset.split(/\s+/);
    if (parts.length === 5) {
      setMinute(parts[0]);
      setHour(parts[1]);
      setDayOfMonth(parts[2]);
      setMonth(parts[3]);
      setDayOfWeek(parts[4]);
    }
  };

  const presets = [
    { label: 'Every minute', value: '* * * * *' },
    { label: 'Every hour', value: '0 * * * *' },
    { label: 'Every day at midnight', value: '0 0 * * *' },
    { label: 'Every day at noon', value: '0 12 * * *' },
    { label: 'Every week (Sunday midnight)', value: '0 0 * * 0' },
    { label: 'Every month (1st at midnight)', value: '0 0 1 * *' },
    { label: 'Every weekday at 9 AM', value: '0 9 * * 1-5' },
    { label: 'Every 5 minutes', value: '*/5 * * * *' },
    { label: 'Every 30 minutes', value: '*/30 * * * *' },
    { label: 'Every 2 hours', value: '0 */2 * * *' },
  ];

  return (
    <Paper sx={{ p: 2, mt: 1 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h6">Cron Expression Builder</Typography>
        <Button
          size="small"
          variant={customMode ? 'outlined' : 'text'}
          onClick={() => setCustomMode(!customMode)}
        >
          {customMode ? 'Use Builder' : 'Custom Expression'}
        </Button>
      </Box>

      {customMode ? (
        <Box>
          <TextField
            fullWidth
            label="Cron Expression"
            value={customExpression}
            onChange={(e) => handleCustomChange(e.target.value)}
            error={error || (customExpression !== '' && !validateCronExpression(customExpression))}
            helperText={
              helperText ||
              (customExpression && !validateCronExpression(customExpression)
                ? 'Invalid cron expression'
                : customExpression && describeCronExpression(customExpression))
            }
            placeholder="0 0 * * *"
          />
        </Box>
      ) : (
        <>
          <Box mb={2}>
            <Typography variant="subtitle2" gutterBottom>
              Quick Presets
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={1}>
              {presets.map((preset) => (
                <Chip
                  key={preset.value}
                  label={preset.label}
                  onClick={() => handlePreset(preset.value)}
                  size="small"
                  variant={value === preset.value ? 'filled' : 'outlined'}
                  color={value === preset.value ? 'primary' : 'default'}
                />
              ))}
            </Box>
          </Box>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Minute</InputLabel>
                <Select
                  value={minute}
                  label="Minute"
                  onChange={(e) => setMinute(e.target.value)}
                >
                  <MenuItem value="0">0 (at the hour)</MenuItem>
                  <MenuItem value="*/5">Every 5 minutes</MenuItem>
                  <MenuItem value="*/15">Every 15 minutes</MenuItem>
                  <MenuItem value="*/30">Every 30 minutes</MenuItem>
                  <MenuItem value="*">Every minute</MenuItem>
                  <MenuItem value="custom">Custom...</MenuItem>
                </Select>
              </FormControl>
              {minute === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Custom Minute"
                  value={minute}
                  onChange={(e) => setMinute(e.target.value)}
                  sx={{ mt: 1 }}
                  placeholder="0-59 or */n"
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Hour</InputLabel>
                <Select
                  value={hour}
                  label="Hour"
                  onChange={(e) => setHour(e.target.value)}
                >
                  <MenuItem value="0">0 (midnight)</MenuItem>
                  <MenuItem value="6">6 (6 AM)</MenuItem>
                  <MenuItem value="9">9 (9 AM)</MenuItem>
                  <MenuItem value="12">12 (noon)</MenuItem>
                  <MenuItem value="18">18 (6 PM)</MenuItem>
                  <MenuItem value="*/2">Every 2 hours</MenuItem>
                  <MenuItem value="*/6">Every 6 hours</MenuItem>
                  <MenuItem value="*/12">Every 12 hours</MenuItem>
                  <MenuItem value="*">Every hour</MenuItem>
                  <MenuItem value="custom">Custom...</MenuItem>
                </Select>
              </FormControl>
              {hour === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Custom Hour"
                  value={hour}
                  onChange={(e) => setHour(e.target.value)}
                  sx={{ mt: 1 }}
                  placeholder="0-23 or */n"
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Day of Month</InputLabel>
                <Select
                  value={dayOfMonth}
                  label="Day of Month"
                  onChange={(e) => setDayOfMonth(e.target.value)}
                >
                  <MenuItem value="*">Every day</MenuItem>
                  <MenuItem value="1">1st of month</MenuItem>
                  <MenuItem value="15">15th of month</MenuItem>
                  <MenuItem value="?">Any day (use with day-of-week)</MenuItem>
                  <MenuItem value="custom">Custom...</MenuItem>
                </Select>
              </FormControl>
              {dayOfMonth === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Custom Day of Month"
                  value={dayOfMonth}
                  onChange={(e) => setDayOfMonth(e.target.value)}
                  sx={{ mt: 1 }}
                  placeholder="1-31, */n, or ?"
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Month</InputLabel>
                <Select
                  value={month}
                  label="Month"
                  onChange={(e) => setMonth(e.target.value)}
                >
                  <MenuItem value="*">Every month</MenuItem>
                  <MenuItem value="1">January</MenuItem>
                  <MenuItem value="2">February</MenuItem>
                  <MenuItem value="3">March</MenuItem>
                  <MenuItem value="4">April</MenuItem>
                  <MenuItem value="5">May</MenuItem>
                  <MenuItem value="6">June</MenuItem>
                  <MenuItem value="7">July</MenuItem>
                  <MenuItem value="8">August</MenuItem>
                  <MenuItem value="9">September</MenuItem>
                  <MenuItem value="10">October</MenuItem>
                  <MenuItem value="11">November</MenuItem>
                  <MenuItem value="12">December</MenuItem>
                  <MenuItem value="custom">Custom...</MenuItem>
                </Select>
              </FormControl>
              {month === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Custom Month"
                  value={month}
                  onChange={(e) => setMonth(e.target.value)}
                  sx={{ mt: 1 }}
                  placeholder="1-12, JAN-DEC, or */n"
                />
              )}
            </Grid>

            <Grid size={{ xs: 12, sm: 6, md: 4 }}>
              <FormControl fullWidth>
                <InputLabel>Day of Week</InputLabel>
                <Select
                  value={dayOfWeek}
                  label="Day of Week"
                  onChange={(e) => setDayOfWeek(e.target.value)}
                >
                  <MenuItem value="*">Every day</MenuItem>
                  <MenuItem value="0">Sunday</MenuItem>
                  <MenuItem value="1">Monday</MenuItem>
                  <MenuItem value="2">Tuesday</MenuItem>
                  <MenuItem value="3">Wednesday</MenuItem>
                  <MenuItem value="4">Thursday</MenuItem>
                  <MenuItem value="5">Friday</MenuItem>
                  <MenuItem value="6">Saturday</MenuItem>
                  <MenuItem value="1-5">Weekdays (Mon-Fri)</MenuItem>
                  <MenuItem value="0,6">Weekends (Sat-Sun)</MenuItem>
                  <MenuItem value="?">Any day (use with day-of-month)</MenuItem>
                  <MenuItem value="custom">Custom...</MenuItem>
                </Select>
              </FormControl>
              {dayOfWeek === 'custom' && (
                <TextField
                  fullWidth
                  size="small"
                  label="Custom Day of Week"
                  value={dayOfWeek}
                  onChange={(e) => setDayOfWeek(e.target.value)}
                  sx={{ mt: 1 }}
                  placeholder="0-7, SUN-SAT, or ?"
                />
              )}
            </Grid>
          </Grid>

          <Box mt={2}>
            <Typography variant="body2" color="text.secondary">
              <strong>Expression:</strong> {customExpression}
            </Typography>
            {customExpression && validateCronExpression(customExpression) && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                <strong>Description:</strong> {describeCronExpression(customExpression)}
              </Typography>
            )}
          </Box>
        </>
      )}
    </Paper>
  );
};
